import { Box, Button, createMultiStyleConfigHelpers, Flex, useMultiStyleConfig } from '@chakra-ui/react';
import { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const parts = ['container', 'indicator', 'option'] as const;

const { definePartsStyle, defineMultiStyleConfig } = createMultiStyleConfigHelpers(parts);

const baseStyle = definePartsStyle({
  container: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'form/bg:disable',
    borderRadius: 'full',
    padding: '4px',
    gap: '4px',
    width: 'fit-content',
    position: 'relative'
  },
  indicator: {
    borderRadius: 'full',
    backgroundColor: 'white',
    top: '4px',
    bottom: '4px',
    position: 'absolute',
    zIndex: 0,
    transition: '200ms ease',
    boxShadow: 'sm',
    _hover: {
      backgroundColor: 'background/secondary'
    }
  },
  option: {
    color: 'text/secondary',
    fontWeight: 'bold',
    borderRadius: 'full',
    padding: '8px',
    cursor: 'pointer',
    zIndex: 1,
    transition: '200ms',
    _hover: {
      color: 'text/link:hover'
    },
    _disabled: {
      cursor: 'not-allowed',
      color: 'text/disabled',
      backgroundColor: 'transparent'
    }
  }
});

export const textSwitchTheme = defineMultiStyleConfig({
  baseStyle
});

type TextSwitchProps = {
  data: Array<{ label: ReactNode; value: string; disabled?: boolean }>;
  value?: string;
  onChange?: (value: string) => void;
};

const TextSwitch = ({ data, value, onChange }: TextSwitchProps) => {
  const [activeValue, setActiveValue] = useState(value || data[0]?.value);
  const [isActiveHovered, setIsActiveHovered] = useState(false);
  const styles = useMultiStyleConfig('TextSwitch');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const activeValueRef = useRef<HTMLButtonElement | null>(null);
  const [indicatorStyles, setIndicatorStyles] = useState<{
    // strings are used because certain number values correspond to measurements in chakra (https://v2.chakra-ui.com/docs/styled-system/theme#spacing), which automatically get converted to some other value which messes up the alignment
    width: string;
    left: string;
  }>({
    width: '0px',
    left: '0px'
  });

  const updateIndicatorStyles = useCallback(() => {
    if (!activeValueRef.current) {
      return;
    }

    setIndicatorStyles({
      width: `${activeValueRef.current.offsetWidth}px`,
      left: `${activeValueRef.current.offsetLeft}px`
    });
  }, []);

  useLayoutEffect(() => {
    updateIndicatorStyles();
  }, [activeValue, data, updateIndicatorStyles]);

  useEffect(() => {
    let resizeObserver: ResizeObserver | undefined;

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        updateIndicatorStyles();
      });

      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }

      if (activeValueRef.current) {
        resizeObserver.observe(activeValueRef.current);
      }
    }

    return () => {
      resizeObserver?.disconnect();
    };
  }, [activeValue, updateIndicatorStyles]);

  useEffect(() => {
    window.addEventListener('resize', updateIndicatorStyles);

    return () => {
      window.removeEventListener('resize', updateIndicatorStyles);
    };
  }, [updateIndicatorStyles]);

  return (
    <Flex ref={containerRef} __css={styles.container!}>
      <Box
        data-testid="text-switch-indicator"
        __css={{
          ...styles.indicator!,
          ...indicatorStyles,
          ...(isActiveHovered ? { backgroundColor: 'background/secondary' } : {})
        }}
      />

      {data.map((item) => (
        <Button
          key={item.value}
          onClick={() => {
            setActiveValue(item.value);
            onChange?.(item.value);
            setIsActiveHovered(true);
          }}
          __css={{
            ...styles.option!,
            ...(activeValue === item.value
              ? {
                  color: 'text/body'
                }
              : {})
          }}
          isDisabled={item.disabled ?? false}
          onMouseEnter={() => {
            if (activeValue === item.value) {
              setIsActiveHovered(true);
            } else {
              setIsActiveHovered(false);
            }
          }}
          onMouseLeave={() => {
            if (activeValue === item.value) {
              setIsActiveHovered(false);
            }
          }}
          ref={
            activeValue === item.value
              ? (node) => {
                  activeValueRef.current = node;
                }
              : undefined
          }
          data-testid={`text-switch-option-${item.value}`}
        >
          {item.label}
        </Button>
      ))}
    </Flex>
  );
};

export default TextSwitch;
